import { useQuery } from "@tanstack/react-query";

import { getOverview, isRegion, type FreshnessMeta } from "./api";
import { OverviewTab } from "./OverviewTab";
import { RegionTab } from "./RegionTab";

/**
 * The freshness bridge (native-module contract step 2).
 *
 * `ModuleViewport` renders one level ABOVE this module's tab content, so the
 * module cannot hand its S8 `meta` up as a prop. This hook is that channel, and
 * it takes an `enabled` flag rather than being called inside a branch -
 * `useNativeModuleMeta` calls it on every render for every module, native or
 * docked, and a conditional call there breaks the rules of hooks for every
 * OTHER module, not only this one.
 *
 * The overview read is the source: it is the only one present on all four tabs,
 * and it shares the exact query key `OverviewTab` uses, so the bridge and the
 * tab are one cache entry rather than two round trips for the same JSON.
 */
export function useProfitYieldMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const head = useQuery({
    queryKey: ["meteoric_profit_yield", "overview"],
    queryFn: getOverview,
    enabled,
  });
  return enabled ? head.data?.meta : undefined;
}

/**
 * Profit Yield, rendered natively in the portal.
 *
 * `decision` and `ownerName` are part of the native-module props and are
 * deliberately NOT read here: permission rendering is shell-owned (I-7), and
 * `ShellPage` has already wrapped everything this returns in `PermissionGate`.
 * A module that branches on `decision` is a second permission renderer, and two
 * renderers drift - which is exactly the failure the native-module contract
 * cites, where a docked dashboard's own grant store denied two people access.
 *
 * An unrecognised `activeTabKey` falls back to Overview rather than rendering
 * nothing: the shell owns the tab bar, so a key this module does not know means
 * the manifest and the code have drifted, and a blank viewport hides that while
 * the Overview shows something true.
 */
export function ProfitYieldModule({
  activeTabKey,
  areaName,
}: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  if (activeTabKey && isRegion(activeTabKey)) {
    return <RegionTab region={activeTabKey} areaName={areaName} />;
  }
  return <OverviewTab areaName={areaName} />;
}
